/**
 * Mobile Product Gallery Carousel
 * Track + translateX, arrows, swipe (50px threshold), 1/N indicator
 */

(function() {
  'use strict';

  // Wait for DOM to be ready
  function initMobileGallery() {
    const gallery = document.querySelector('[data-mobile-gallery]');
    if (!gallery) {
      console.warn('[Mobile Gallery] Gallery element not found - may be desktop view or not loaded yet');
      return;
    }

    console.log('[Mobile Gallery] Initializing mobile carousel...');

    const hero = gallery.querySelector('[data-hero]');
    const track = gallery.querySelector('[data-carousel-track]');
    if (!track) {
      console.error('[Mobile Gallery] Track not found');
      return;
    }
  
    const slides = Array.from(track.querySelectorAll('.pdp-carousel__slide'));
    const prevButton = gallery.querySelector('[data-nav="prev"]');
    const nextButton = gallery.querySelector('[data-nav="next"]');
    const indicator = gallery.querySelector('.m-indicator');
    const indicatorCurrent = indicator?.querySelector('.m-indicator__current');
    const indicatorTotal = indicator?.querySelector('.m-indicator__total');

    if (slides.length === 0) {
      console.warn('[Mobile Gallery] No slides found');
      return;
    }

    const SWIPE_THRESHOLD = 50;
    const TRANSITION_MS = 250;
    let currentIndex = 0;
    let touchStartX = null;
    let touchStartY = null;
    let isTransitioning = false;
    let visibleSlides = slides; // All slides visible by default

    // Ensure track width is correct (each slide is 100% of container)
    // Track must be wide enough to hold all slides side by side
    track.style.width = `${slides.length * 100}%`;
    
    // Also set CSS variable for potential CSS use
    if (hero) {
      hero.style.setProperty('--slide-count', slides.length);
    }

    function setTrackPosition(index) {
      if (!track) {
        console.error('[Mobile Gallery] Track is null');
        return;
      }
      
      // Clamp index to valid range
      let i = Math.max(0, Math.min(index, slides.length - 1));
      currentIndex = i;
      
      // FIXED: Use visibleSlides for calculation when filtered
      // When slides are filtered, we need to calculate based on visible slides
      const activeSlides = visibleSlides.length > 0 ? visibleSlides : slides;
      const activeCount = activeSlides.length;
      
      if (activeCount > 0) {
        // Find the position of the current slide within visible slides
        const currentSlide = slides[i];
        const visibleIndex = activeSlides.indexOf(currentSlide);
        
        // If current slide is not visible, use first visible slide
        const targetVisibleIndex = visibleIndex >= 0 ? visibleIndex : 0;
        const targetSlide = activeSlides[targetVisibleIndex];
        const targetAbsoluteIndex = slides.indexOf(targetSlide);
        
        // Update currentIndex to the target absolute index
        currentIndex = targetAbsoluteIndex >= 0 ? targetAbsoluteIndex : i;
        
        // Calculate transform based on visible slides count
        // Each visible slide takes 100% of container width
        // Track width is activeCount * 100%
        // To show slide at position targetVisibleIndex, translate by: -targetVisibleIndex * (100 / activeCount)%
        const translatePercent = -(targetVisibleIndex * (100 / activeCount));
        track.style.transform = `translateX(${translatePercent}%)`;
        track.style.webkitTransform = `translateX(${translatePercent}%)`;
        console.log('[Mobile Gallery] Set position - visible index:', targetVisibleIndex, 'of', activeCount, 'absolute index:', currentIndex, 'translateX:', translatePercent + '%');
      }
      
      // Update indicator
      const visibleCount = activeCount;
      if (indicator) {
        const currentVisibleIndex = activeSlides.indexOf(slides[currentIndex]);
        const safeIndex = currentVisibleIndex >= 0 ? currentVisibleIndex : 0;
        
        let dots = indicator.querySelectorAll('.m-indicator__dot');
        if (dots.length !== visibleCount) {
          indicator.innerHTML = '';
          for (let d = 0; d < visibleCount; d++) {
            const dot = document.createElement('div');
            dot.className = 'm-indicator__dot' + (d === safeIndex ? ' m-indicator__dot--active' : '');
            indicator.appendChild(dot);
          }
        } else {
          dots.forEach((dot, idx) => {
            if (idx === safeIndex) {
              dot.classList.add('m-indicator__dot--active');
            } else {
              dot.classList.remove('m-indicator__dot--active');
            }
          });
        }
      }
      
      // Update aria attributes
      slides.forEach((s, idx) => {
        const isVisible = activeSlides.includes(s);
        const visiblePos = isVisible ? activeSlides.indexOf(s) + 1 : 0;
        s.setAttribute('aria-label', `Image ${visiblePos} of ${visibleCount}`);
        if (idx === currentIndex && isVisible) {
          s.setAttribute('aria-current', 'true');
        } else {
          s.removeAttribute('aria-current');
        }
      });
    }

    function updateSlide(index) {
      // Don't block if transitioning to a different slide
      if (isTransitioning && index === currentIndex) {
        console.log('[Mobile Gallery] Update blocked - already transitioning to this slide');
        return;
      }
      
      // Clamp index
      let i = index;
      if (i < 0) i = slides.length - 1;
      if (i >= slides.length) i = 0;
      
      if (i === currentIndex) {
        console.log('[Mobile Gallery] Already at index', i);
        return;
      }
      
      console.log('[Mobile Gallery] Updating slide from', currentIndex, 'to', i);
      
      // Ensure transition is enabled
      track.style.transition = `transform ${TRANSITION_MS}ms ease`;
      track.style.webkitTransition = `transform ${TRANSITION_MS}ms ease`;
      
      setTrackPosition(i);
      preloadAdjacentImages(i);
    }

    function preloadAdjacentImages(index) {
      [index - 1, index + 1].forEach(offset => {
        const i = offset < 0 ? slides.length - 1 : offset >= slides.length ? 0 : offset;
        const img = slides[i]?.querySelector('img');
        if (img && img.loading === 'lazy') img.loading = 'eager';
      });
    }

    function next() {
      if (isTransitioning) {
        console.log('[Mobile Gallery] Next blocked - transitioning');
        return;
      }
      console.log('[Mobile Gallery] Next called, currentIndex:', currentIndex, 'total slides:', slides.length);
      isTransitioning = true;
      
      // Navigate through visible slides only
      if (visibleSlides.length > 0) {
        const currentVisibleIdx = visibleSlides.indexOf(slides[currentIndex]);
        const nextVisibleIdx = (currentVisibleIdx + 1) % visibleSlides.length;
        const nextSlide = visibleSlides[nextVisibleIdx];
        const nextIndex = slides.indexOf(nextSlide);
        updateSlide(nextIndex);
      } else {
        // Fallback to all slides
        let nextIdx = currentIndex + 1;
        if (nextIdx >= slides.length) {
          nextIdx = 0;
        }
        updateSlide(nextIdx);
      }
      
      setTimeout(() => { 
        isTransitioning = false;
        console.log('[Mobile Gallery] Transition complete, new index:', currentIndex);
      }, TRANSITION_MS);
    }

    function prev() {
      if (isTransitioning) {
        console.log('[Mobile Gallery] Prev blocked - transitioning');
        return;
      }
      console.log('[Mobile Gallery] Prev called, currentIndex:', currentIndex, 'total slides:', slides.length);
      isTransitioning = true;
      
      // Navigate through visible slides only
      if (visibleSlides.length > 0) {
        const currentVisibleIdx = visibleSlides.indexOf(slides[currentIndex]);
        const prevVisibleIdx = currentVisibleIdx === 0 ? visibleSlides.length - 1 : currentVisibleIdx - 1;
        const prevSlide = visibleSlides[prevVisibleIdx];
        const prevIndex = slides.indexOf(prevSlide);
        updateSlide(prevIndex);
      } else {
        // Fallback to all slides
        let prevIdx = currentIndex - 1;
        if (prevIdx < 0) {
          prevIdx = slides.length - 1;
        }
        updateSlide(prevIdx);
      }
      
      setTimeout(() => { 
        isTransitioning = false;
        console.log('[Mobile Gallery] Transition complete, new index:', currentIndex);
      }, TRANSITION_MS);
    }

    function goToMediaId(mediaId) {
      if (!mediaId) {
        console.warn('[Mobile Gallery] goToMediaId called with no mediaId');
        return;
      }
      
      const mediaIdStr = String(mediaId);
      console.log('[Mobile Gallery] goToMediaId - looking for mediaId:', mediaIdStr);
      
      // First, try to find in all slides
      let idx = slides.findIndex(s => {
        const slideMediaId = String(s.dataset.mediaId);
        return slideMediaId === mediaIdStr;
      });
      
      // If not found, try visible slides only
      if (idx === -1 && visibleSlides.length > 0) {
        const visibleSlide = visibleSlides.find(s => String(s.dataset.mediaId) === mediaIdStr);
        if (visibleSlide) {
          idx = slides.indexOf(visibleSlide);
          console.log('[Mobile Gallery] Found in visible slides, index:', idx);
        }
      }
      
      if (idx !== -1) {
        if (idx !== currentIndex) {
          console.log('[Mobile Gallery] Moving to slide index', idx, 'from', currentIndex);
          isTransitioning = true;
          updateSlide(idx);
          setTimeout(() => { 
            isTransitioning = false;
            console.log('[Mobile Gallery] Successfully moved to slide', idx);
          }, TRANSITION_MS);
        } else {
          console.log('[Mobile Gallery] Already on slide', idx);
        }
      } else {
        console.warn('[Mobile Gallery] Media ID not found in any slides:', mediaIdStr);
        console.log('[Mobile Gallery] Available media IDs:', slides.map(s => s.dataset.mediaId));
      }
    }

    function handleTouchStart(e) {
      touchStartX = e.touches[0].clientX;
      touchStartY = e.touches[0].clientY;
    }

    function handleTouchEnd(e) {
      if (touchStartX == null) return;
      const touchEndX = e.changedTouches[0].clientX;
      const touchEndY = e.changedTouches[0].clientY;
      const deltaX = touchEndX - touchStartX;
      const deltaY = touchEndY - touchStartY;
      if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > SWIPE_THRESHOLD) {
        if (deltaX > 0) prev();
        else next();
      }
      touchStartX = null;
    }

    function handleKeyDown(e) {
      if (!hero.contains(document.activeElement)) return;
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        prev();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        next();
      }
    }

    // Robust button handlers - use addEventListener for better reliability
    function attachButtonHandlers(button, direction) {
      if (!button) {
        console.warn('[Mobile Gallery]', direction, 'button not found');
        return;
      }
      
      // Ensure button has proper attributes
      button.setAttribute('type', 'button');
      button.setAttribute('role', 'button');
      button.setAttribute('tabindex', '0');
      button.style.pointerEvents = 'auto';
      button.style.cursor = 'pointer';
      
      // Handler function
      function handleAction(e) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        console.log('[Mobile Gallery]', direction, 'button activated');
        
        if (direction === 'prev') {
          prev();
        } else {
          next();
        }
        
        return false;
      }
      
      // Remove any existing handlers by cloning (clean slate)
      const newButton = button.cloneNode(true);
      button.parentNode.replaceChild(newButton, button);
      const btn = gallery.querySelector(`[data-nav="${direction === 'prev' ? 'prev' : 'next'}"]`);
      
      if (!btn) {
        console.error('[Mobile Gallery] Could not find button after clone');
        return;
      }
      
      // Add click handler (works on desktop and mobile)
      btn.addEventListener('click', handleAction, { passive: false });
      
      // Add touchend handler (mobile-specific, more reliable than click on some devices)
      btn.addEventListener('touchend', handleAction, { passive: false });
      
      console.log('[Mobile Gallery]', direction, 'button handlers attached successfully');
    }
    
    // Attach handlers
    attachButtonHandlers(prevButton, 'prev');
    attachButtonHandlers(nextButton, 'next');
    
    if (hero) {
      hero.addEventListener('touchstart', handleTouchStart, { passive: true });
      hero.addEventListener('touchend', handleTouchEnd, { passive: true });
      hero.setAttribute('tabindex', '0');
      hero.addEventListener('keydown', handleKeyDown);
    }

    /**
     * Filter slides by color code (similar to desktop gallery)
     */
    function filterSlidesByColorCode(colorCode) {
      console.log('[Mobile Gallery] filterSlidesByColorCode called with:', colorCode);
      
      if (!colorCode || !window.productData) {
        // Show all slides if no color code
        visibleSlides = slides;
        slides.forEach(slide => {
          slide.style.display = '';
        });
        // Update track width
        track.style.width = `${visibleSlides.length * 100}%`;
        console.log('[Mobile Gallery] Showing all slides (no color code)');
        return;
      }
      
      const codeLower = String(colorCode).toLowerCase().trim();
      const matchingMediaIds = [];
      
      if (window.productData.media) {
        window.productData.media.forEach(img => {
          let matches = false;
          
          // Check color_code metafield
          if (img.color_code) {
            const imgCode = (typeof img.color_code === 'string' ? img.color_code : img.color_code.value || '').toLowerCase().trim();
            if (imgCode === codeLower) {
              matches = true;
            }
          }
          
          // Check alt_text for color code pattern
          if (!matches && img.alt_text) {
            const altText = img.alt_text;
            const threeDigitPattern = /\b(\d{3})\b/g;
            const matches_alt = altText.match(threeDigitPattern);
            if (matches_alt) {
              for (const match of matches_alt) {
                if (match === colorCode || match === codeLower) {
                  matches = true;
                  break;
                }
              }
            }
            // Also check if alt text directly contains the color code
            if (!matches && altText.toLowerCase().includes(codeLower)) {
              const codePattern = new RegExp(`(^|[^0-9])${codeLower.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}([^0-9]|$)`);
              if (codePattern.test(altText.toLowerCase())) {
                matches = true;
              }
            }
          }
          
          if (matches) {
            matchingMediaIds.push(img.id);
          }
        });
      }
      
      console.log('[Mobile Gallery] Found', matchingMediaIds.length, 'matching media IDs for color code:', colorCode);
      
      // Show/hide slides based on matches
      slides.forEach(slide => {
        const mediaId = Number(slide.dataset.mediaId);
        if (matchingMediaIds.length > 0) {
          const shouldShow = matchingMediaIds.includes(mediaId);
          slide.style.display = shouldShow ? '' : 'none';
        } else {
          slide.style.display = ''; // Show all if no matches
        }
      });
      
    // Update visible slides array
    visibleSlides = slides.filter(s => s.style.display !== 'none');
    
    // Update track width based on visible slides
    const visibleCount = visibleSlides.length > 0 ? visibleSlides.length : slides.length;
    track.style.width = `${visibleCount * 100}%`;
    
    // If current slide is hidden, switch to first available
    if (visibleSlides.length > 0 && !visibleSlides.includes(slides[currentIndex])) {
      const firstVisible = visibleSlides[0];
      const firstIndex = slides.indexOf(firstVisible);
      console.log('[Mobile Gallery] Current slide hidden, switching to first visible at index', firstIndex);
      // Use requestAnimationFrame to ensure DOM has updated
      requestAnimationFrame(() => {
        setTrackPosition(firstIndex);
      });
    } else if (visibleSlides.length > 0) {
      // Recalculate position for current slide to ensure proper centering
      requestAnimationFrame(() => {
        setTrackPosition(currentIndex);
      });
    }
    
    console.log('[Mobile Gallery] Filtered to', visibleSlides.length, 'slides for color code:', colorCode);
    }

    // Listen for variant changes - Multiple listeners to catch all events
    function handleVariantChange(e) {
      const variant = e.detail?.variant;
      console.log('[Mobile Gallery] Variant change event received:', variant);
      
      if (!variant) {
        console.warn('[Mobile Gallery] Variant change event has no variant');
        return;
      }
      
      // Filter by color code if available
      if (variant.color_code) {
        const colorCode = typeof variant.color_code === 'string' 
          ? variant.color_code 
          : variant.color_code.value || '';
        if (colorCode) {
          console.log('[Mobile Gallery] Filtering by color code:', colorCode);
          filterSlidesByColorCode(colorCode);
        } else {
          console.log('[Mobile Gallery] Variant has color_code but it is empty');
        }
      } else {
        // Show all slides if no color code
        console.log('[Mobile Gallery] No color_code on variant, showing all slides');
        filterSlidesByColorCode(null);
      }
      
      // Jump to variant's featured image
      if (variant.featured_media && variant.featured_media.id) {
        console.log('[Mobile Gallery] Jumping to featured media:', variant.featured_media.id);
        setTimeout(() => {
          goToMediaId(variant.featured_media.id);
        }, 200); // Delay to ensure filtering is done first
      } else {
        console.log('[Mobile Gallery] Variant has no featured_media');
      }
    }
    
    // Listen to variant:change event (primary method)
    document.addEventListener('variant:change', handleVariantChange);
    console.log('[Mobile Gallery] Listening for variant:change events');
    
    // Also listen to form changes as fallback
    const productForm = document.getElementById('product-form');
    if (productForm) {
      const variantInput = productForm.querySelector('#product-variant-id');
      if (variantInput) {
        // Use both change and input events
        variantInput.addEventListener('change', function() {
          const variantId = this.value;
          if (window.productData && window.productData.variants) {
            const variant = window.productData.variants.find(v => v.id == variantId);
            if (variant) {
              console.log('[Mobile Gallery] Variant changed via form input (change event):', variant);
              handleVariantChange({ detail: { variant } });
            }
          }
        });
        
        variantInput.addEventListener('input', function() {
          const variantId = this.value;
          if (window.productData && window.productData.variants) {
            const variant = window.productData.variants.find(v => v.id == variantId);
            if (variant) {
              console.log('[Mobile Gallery] Variant changed via form input (input event):', variant);
              handleVariantChange({ detail: { variant } });
            }
          }
        });
        console.log('[Mobile Gallery] Listening for form input changes');
      } else {
        console.warn('[Mobile Gallery] Variant input not found in form');
      }
    } else {
      console.warn('[Mobile Gallery] Product form not found');
    }
    
    // Direct connection to desktop gallery update function
    // This ensures mobile carousel updates when desktop gallery updates
    if (window.cfwVariantPicker && window.cfwVariantPicker.updateGallery) {
      const originalUpdateGallery = window.cfwVariantPicker.updateGallery;
      window.cfwVariantPicker.updateGallery = function(variant) {
        // Call original function
        originalUpdateGallery.call(this, variant);
        // Also update mobile carousel
        console.log('[Mobile Gallery] Desktop gallery updated, syncing mobile carousel');
        if (variant && variant.featured_media && variant.featured_media.id) {
          setTimeout(() => {
            goToMediaId(variant.featured_media.id);
          }, 100);
        }
      };
      console.log('[Mobile Gallery] Connected to desktop gallery update function');
    } else {
      console.warn('[Mobile Gallery] Desktop gallery update function not found');
    }

    // Listen for main image updates (desktop gallery changes)
    const mainImage = document.getElementById('pdp-main-image');
    if (mainImage) {
      const observer = new MutationObserver(function(mutations) {
        mutations.forEach(function(m) {
          if (m.attributeName === 'data-media-id' && mainImage.dataset.mediaId) {
            console.log('[Mobile Gallery] Main image changed to media ID:', mainImage.dataset.mediaId);
            goToMediaId(mainImage.dataset.mediaId);
          }
        });
      });
      observer.observe(mainImage, { attributes: true, attributeFilter: ['data-media-id'] });
      console.log('[Mobile Gallery] Watching main image for changes');
    } else {
      console.warn('[Mobile Gallery] Main image element not found');
    }

    // Helper to recalculate position on resize
    let resizeTimeout;
    function handleResize() {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        console.log('[Mobile Gallery] Window resized, recalculating position');
        setTrackPosition(currentIndex);
      }, 150);
    }
    window.addEventListener('resize', handleResize);
    
    // Improved initialization - wait for DOM and productData
    function initializeCarousel() {
      // Set initial position
      requestAnimationFrame(() => {
        setTimeout(() => {
          setTrackPosition(0);
          console.log('[Mobile Gallery] Initial position set to index 0');
        }, 100);
      });
      
      // Wait for productData to be available, then apply initial filter
      function initializeWithVariant() {
        if (!window.productData) {
          console.log('[Mobile Gallery] productData not available yet, will retry');
          return false;
        }
        
        if (!window.productData.variants || window.productData.variants.length === 0) {
          console.log('[Mobile Gallery] No variants in productData');
          return false;
        }
        
        const variantIdInput = document.getElementById('product-variant-id');
        if (!variantIdInput || !variantIdInput.value) {
          console.log('[Mobile Gallery] No variant selected yet');
          return false;
        }
        
        const currentVariant = window.productData.variants.find(v => v.id == variantIdInput.value);
        if (!currentVariant) {
          console.log('[Mobile Gallery] Variant not found in productData');
          return false;
        }
        
        console.log('[Mobile Gallery] Initializing with variant:', currentVariant);
        
        // Filter by color code if available
        if (currentVariant.color_code) {
          const colorCode = typeof currentVariant.color_code === 'string' 
            ? currentVariant.color_code 
            : currentVariant.color_code.value || '';
          if (colorCode) {
            filterSlidesByColorCode(colorCode);
          }
        }
        
        // Jump to featured image if available
        if (currentVariant.featured_media && currentVariant.featured_media.id) {
          setTimeout(() => {
            goToMediaId(currentVariant.featured_media.id);
          }, 200);
        }
        
        return true;
      }
      
      // Try initialization multiple times with increasing delays
      let attempts = 0;
      const maxAttempts = 10;
      
      function tryInitialize() {
        attempts++;
        if (initializeWithVariant()) {
          console.log('[Mobile Gallery] Successfully initialized with variant');
        } else if (attempts < maxAttempts) {
          setTimeout(tryInitialize, 200);
        } else {
          console.warn('[Mobile Gallery] Failed to initialize with variant after', maxAttempts, 'attempts');
        }
      }
      
      // Start trying after DOM is ready
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => {
          setTimeout(tryInitialize, 100);
        });
      } else {
        setTimeout(tryInitialize, 100);
      }
    }
    
    // Start initialization
    initializeCarousel();
    
    console.log('[Mobile Gallery] Script loaded, initializing with', slides.length, 'slides');

    // Expose API
    window.mobileGallery = {
      next,
      prev,
      goTo: updateSlide,
      goToMediaId,
      currentIndex: () => currentIndex,
      filterSlidesByColorCode
    };
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initMobileGallery);
  } else {
    // DOM already ready, but wait a tick to ensure all scripts are loaded
    setTimeout(initMobileGallery, 0);
  }
})();
